import React, { useEffect, useRef } from "react";
import { language } from "../../../languageProvider/language/language";
import { defaultLanguage } from "../../../helper/utility";

/**
 * Second-level horizontal chips row: sub-categories of the category currently
 * selected in CategoryChipsRow. Mobile-only, rendered inside .pf-sticky-stack
 * right below the category row.
 *
 * Returns null when the selected category has no (non-empty) sub-category, so
 * the sticky stack keeps its normal two-row height on flat catalogues.
 *
 * Props:
 *  - subCategories: Array<{ name, count }>  // already filtered/ordered by the caller
 *  - selectedSubCategoryName: string ("" = "Tutte")
 *  - onSelect(nameOrNull): cb
 */
const SubCategoryChipsRow = ({
  subCategories = [],
  selectedSubCategoryName = "",
  onSelect,
}) => {
  const scrollerRef = useRef(null);
  const activeRef = useRef(null);

  // Keep the active chip visible: the row scrolls horizontally and the picked
  // sub-category is often out of view (long labels, many entries).
  useEffect(() => {
    const scroller = scrollerRef.current;
    const active = activeRef.current;
    if (!scroller || !active) return;
    const left =
      active.offsetLeft - scroller.clientWidth / 2 + active.offsetWidth / 2;
    scroller.scrollTo({ left: Math.max(0, left), behavior: "smooth" });
  }, [selectedSubCategoryName]);

  if (!subCategories.length) return null;

  const L = (key, fallback) =>
    (language[defaultLanguage] && language[defaultLanguage][key]) || fallback;

  const allActive = !selectedSubCategoryName;

  return (
    <div
      className="pf-subrow"
      role="region"
      aria-label={L("sottocategorie", "Sottocategorie")}
    >
      <div className="pf-subchips" ref={scrollerRef}>
        <button
          type="button"
          className={`pf-subchip ${allActive ? "pf-subchip--active" : ""}`}
          aria-pressed={allActive}
          ref={allActive ? activeRef : null}
          onClick={() => onSelect && onSelect(null)}
        >
          <span className="pf-subchip__label">{L("tutti", "Tutti")}</span>
        </button>

        {subCategories.map((sub) => {
          const isActive = sub.name === selectedSubCategoryName;
          return (
            <button
              key={sub.name}
              type="button"
              className={`pf-subchip ${isActive ? "pf-subchip--active" : ""}`}
              aria-pressed={isActive}
              ref={isActive ? activeRef : null}
              title={sub.name}
              onClick={() => onSelect && onSelect(sub.name)}
            >
              <span className="pf-subchip__label">{sub.name}</span>
              {sub.count > 0 && (
                <span className="pf-subchip__count">{sub.count}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default SubCategoryChipsRow;
